import { useState, useRef, useEffect } from 'react';
import { FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage';

/**
 * DisplaySentence Component
 * 
 * A reusable component for displaying text with optional audio playback.
 * 
 * @param {string} text - The text to display
 * @param {string} audioUrl - Optional audio URL (can be Firebase Storage URL or direct URL)
 * @param {string} textClassName - Optional custom className for text styling
 * @param {string} buttonClassName - Optional custom className for audio button styling
 * @param {string} containerClassName - Optional custom className for container styling
 */
const DisplaySentence = ({ 
  text, 
  audioUrl,
  resolvedAudioUrl,
  textClassName = "text-lg font-medium text-gray-900",
  buttonClassName = "p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors",
  containerClassName = "flex items-center gap-3"
}) => {
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const audioRef = useRef(null);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const playAudio = async () => {
    if (!resolvedAudioUrl && !audioUrl) return;
    if (audioError) return;
    
    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    
    setAudioLoading(true);
    setAudioError(false);

    const tryPlay = (url) => {
      return new Promise((resolve, reject) => {
        const audio = new Audio(url);
        audioRef.current = audio;
        
        audio.oncanplaythrough = () => {
          audio.play().then(resolve).catch(reject);
        };
        
        audio.onended = () => {
          audioRef.current = null;
          setAudioLoading(false);
          resolve();
        };
        
        audio.onerror = (e) => {
          audioRef.current = null;
          reject(e);
        };

        // Timeout after 10 seconds
        setTimeout(() => reject(new Error('Audio timeout')), 10000);
      });
    };

    try {
      // 1. Try pre-resolved URL first (it's faster)
      if (resolvedAudioUrl) {
        try {
          await tryPlay(resolvedAudioUrl);
          return;
        } catch (err) {
          console.warn("Pre-resolved audio failed, attempting refresh...", err);
          // If pre-resolved failed, fall through to refresh logic if audioUrl exists
          if (!audioUrl) throw err;
        }
      }

      // 2. Refresh token if it's a Firebase URL or if direct path is provided
      if (audioUrl) {
        let finalUrl = audioUrl;
        
        if (audioUrl.includes('firebasestorage.googleapis.com') || !audioUrl.startsWith('http')) {
          try {
            const storage = getStorage();
            // If it's a full URL, extract path. If it's already a path, use it.
            const path = audioUrl.includes('firebasestorage.googleapis.com') 
              ? decodeURIComponent(audioUrl.split('/o/')[1]?.split('?')[0])
              : audioUrl;
              
            const storageRefObj = storageRef(storage, path);
            finalUrl = await getDownloadURL(storageRefObj);
          } catch (refreshErr) {
            console.error("Failed to refresh audio token:", refreshErr);
            // If we can't refresh and already tried resolved, we fail
            if (resolvedAudioUrl) throw refreshErr;
          }
        }
        
        await tryPlay(finalUrl);
      }
    } catch (error) {
      console.error("Audio playback failed finally:", error);
      setAudioError(true);
      setAudioLoading(false);
    }
  };

  if (audioError) {
    return (
      <div className={containerClassName}>
        <span className={textClassName}>{text}</span>
        <button
          className={`${buttonClassName} opacity-40 cursor-not-allowed`}
          title="Audio unavailable"
          disabled
        >
          <FaVolumeMute className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      <span className={textClassName}>{text}</span>
      {(resolvedAudioUrl || audioUrl) && (
        <button
          onClick={playAudio}
          className={buttonClassName}
          title="Play Audio"
          disabled={audioLoading}
        >
          {audioLoading ? (
            <div className="flex items-center gap-0.5 h-4 w-4">
              <div className="w-0.5 bg-current animate-pulse" style={{ height: '40%', animationDelay: '0ms', animationDuration: '600ms' }}></div>
              <div className="w-0.5 bg-current animate-pulse" style={{ height: '100%', animationDelay: '150ms', animationDuration: '600ms' }}></div>
              <div className="w-0.5 bg-current animate-pulse" style={{ height: '60%', animationDelay: '300ms', animationDuration: '600ms' }}></div>
              <div className="w-0.5 bg-current animate-pulse" style={{ height: '80%', animationDelay: '450ms', animationDuration: '600ms' }}></div>
            </div>
          ) : (
            <FaVolumeUp className="w-4 h-4" />
          )}
        </button>
      )}
    </div>
  );
};

export default DisplaySentence;
